import React, { useMemo } from 'react'
import { useSlate } from 'slate-react'
import { useTranslation } from 'react-i18next'
import { getNodeDeserializer } from '@udecode/slate-plugins'
import { MARK_BOLD, MARK_ITALIC, MARK_STRIKETHROUGH, MARK_UNDERLINE } from '@udecode/slate-plugins';
import clsx from 'clsx';

import { MARK_LINE_HEIGHT, MARK_FONT_SIZE, MARK_COLOR, MARK_BACKGROUND_COLOR } from '../../Marks';
import { BLOCK_TABLE_ROW, BLOCK_TABLE_CELL, BLOCK_PARAGRAPH } from '../Table'
import { format, isDate, getKey } from 'utils';
import { agendaFields } from 'components/Modals/AgendaModal';


export function renderVariableElement(params) {

    return (props) => {
      if (props.element.type === 'variable') { 
        return (
            <VariableElement {...props} {...params} />
        );
      }
    
      return undefined;
    }
  
}

export function deserializeVariable({ participants }) {
  const type = 'variable';
  return  {
    element: getNodeDeserializer({
      type,
      rules: [{ className: 'variable' }],
      node: (el) => {
          const { type: variableType, id, participant: participantId, apiname } = el.dataset;
          const participant = participants.get(participantId);
          let value = el.innerHTML;
          if (participant && variableType !== 'constant') {
            const variable = participant.variables.get(id)
            if (variable) {
              value = variable.value?.length ?  variable.value : variable.name
            }
          }

          if (participant && variableType === 'constant') {
            value = apiname;
          }
          
          return ({ 
            type, 
            variableType, 
            participantId: variableType === 'constant' && id === 'contract' ?  undefined : participantId, 
            _id: id, 
            apiName: apiname,
            value: value,
            fontSize: '12px',
            lineHeight: 1.5
          })  
      },
    })
  }; 
}


export const VariablePlugin = ({ contract, participants = new Map(), highlight } = {}) => {
  const type = 'variable';

  return {
      renderElement: renderVariableElement({  contract, participants,highlight }),
      renderElementDeps: [contract, participants, highlight],
      deserialize: deserializeVariable({ contract, participants }),
      inlineTypes: [type],
      voidTypes: [type],
  }
}

const participantConstants = [
  'name',
  'last_name',
  'signedAt',
  'obligation',
  'label',
  'representative',
  'email',
  'cpf',
  'cargo',
  'rg'
]
const documentConstants = [
  'name',
  'documentId',
  'externalId',
  'signedAt'
]


function joinParticipantsNames(participants = [], filterFunc = () => true) {
  if (!participants || !participants.length) return '';
  return participants.filter(filterFunc).map(p => `${p.name} ${p.last_name || ''}`).join(', ')
}

function parseConstant(obj, apiname, locale) {
  if (apiname === 'testamentHeirsNames') {
    return joinParticipantsNames(obj.participants, p => p.testamentRole === 'heir');
  }
  if (apiname === 'testamentWitnessesNames') {
    return joinParticipantsNames(obj.participants, p => p.testamentRole === 'witness');
  }
  const value = getKey(obj, apiname);

  if (apiname === 'signedAt' && value) return format(value, 'PPP', { locale })
  if (Number.isInteger(value)) return String(value)
  return value;
}

const documentPropertiesMapper = {
  signedAt: 'popup_properties_signdate',
  name: 'popup_properties_docname',
  documentId: 'popup_properties_docid',
  externalId: 'popup_properties_externalId',
  testamentHeirsNames: 'testamentHeirsNames',
  testamentWitnessesNames: 'testamentWitnessesNames'
}

function constructDynamicTable(dynamicTable) {
  return {
    type: 'table',
    row: dynamicTable.rows.length,
    col: dynamicTable.headers.length,
    children: [
        {
          type: BLOCK_TABLE_ROW,
          children: dynamicTable.headers.map((column) => {
            return {
              type: BLOCK_TABLE_CELL,
              children: [
                {
                  type: BLOCK_PARAGRAPH,
                  children: [{ text: column }],
                },
              ],
            }
          }),
        },
        dynamicTable.rows.map((row) => {
          return {
            type: BLOCK_TABLE_ROW,
            children: row.columns.map((column) => {
              return {
                type: BLOCK_TABLE_CELL,
                children: [
                  {
                    type: BLOCK_PARAGRAPH,
                    children: [{ text: column }],
                  },
                ],
              }
            }),
          }
        })
    ]
  }
}

function getConstantName(participant, element) {
  if (element?.apiName?.includes('agendaProperties')) {
    const [,propertyName] = element.apiName.split('.');
    return `agenda.${propertyName}_placeholder`
  }
  return `contracts.constants.${participant.obligation === 'legal' && element.apiName === 'name' ? 'company_name' : element.apiName}`
}

function isDateType({ apiName = '', type } = {}) {
 
  if (apiName?.includes('agendaProperties')){
      const [,propertyName] = apiName.split('.');
      const agendaField = agendaFields.find(af => af.propertyName === propertyName);
      return agendaField.type === 'calendar';
  }
  return type === 'date';
}

export function VariableElement({ contract, participants, highlight, element, attributes, children }) {
  const { t } = useTranslation()
  const locale = useMemo(() => contract.language?.toLowerCase?.() || 'es', [contract.language]);
  const jurisdiction = useMemo(() => contract.jurisdiction || 'mx', [contract.jurisdiction]);

  const [participant, variable] = useMemo(() => {
    const participant = participants.get(element.participantId);
    const isConstant = element.variableType === 'constant';
    const isDocumentProperty = !participant && isConstant;
    //  console.log({isConstant, isDocumentProperty, element, contract})
    if (isDocumentProperty) return [{},  {
      ...element,
      value: parseConstant(contract, element.apiName, locale),
      name: t(`editor.variables.${documentPropertiesMapper[element.apiName]}`, t(`contracts.constants.${documentPropertiesMapper[element.apiName]}`,''))
    }]
    if (!participant) return [{}, {}]

    const variable = participant.variables.get(element._id);
      // console.log({participant, element, variable})
    if (isConstant) return [participant, {
      ...element,
      value: parseConstant(participant, element.apiName, locale),
      name: t(getConstantName(participant, element), '', {context: jurisdiction})
    }]
    if (!variable)  {
      // removeNodesById(editor, element._id);
      return [participant, {}]
    }
    
    return [participant, {...variable, variableType: variable.type }]
  }, [element, participants, contract, locale, t])

  const value = useMemo(() => {
     if (isDateType({ apiName: variable.apiName, type: variable.type }) && isDate(variable?.value)) return format(variable.value, 'PPP', { locale });
     if (variable?.type === 'dynamictable') return variable.valueHtml || variable.name;
    return variable.value?.length > 0 ? variable.value : variable.name;
  }, [variable.type, variable.value, variable.valueHtml, variable.apiName, variable.name, locale])

  // console.log({variable})


    return (
        <span 
            {...attributes}
            className={clsx(highlight && "variable")}
            style={{
              lineHeight: element[MARK_LINE_HEIGHT], 
              fontSize: element[MARK_FONT_SIZE], 
              fontColor: element[MARK_COLOR], 
              backgroundColor: element[MARK_BACKGROUND_COLOR], 
              fontWeight: element[MARK_BOLD] ? 'bold' : 'normal', 
              fontStyle: element[MARK_ITALIC] ? 'italic' : '', 
              textDecoration: `${element[MARK_UNDERLINE] ? 'underline' : ''} ${element[MARK_STRIKETHROUGH] ? 'line-through' : ''}`, 
              userSelect: 'none',
            }}
            data-index={element.variableType === 'constant' && !element.participantId ? 'participantproperty' : `participant${participant.index}`}
            contentEditable={false}
            data-participant={element.participantId ? element.participantId : 'property'} 
            data-id={element._id}
            data-element-id={element._id}
            data-apiname={variable.apiName}
            data-type={variable.variableType}
            data-slate-value={value}
        >
            {variable.type === 'dynamictable' ? <div dangerouslySetInnerHTML={{ __html: value }} /> : value}
            {children}
        </span>
    )
}